import { useLayoutEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { useSearchParams } from "react-router-dom";
import { useCountries } from "../shared/CountryTextField/CountriesContext";
import type { CountryListItem } from "../../data/dtos/get-registration-countries.dto";
import { FormType } from "./types";
import { getPrefillFromSearchParams } from "./utils/queryParamPrefill";
import { statesUS } from "./countriesList";
import { INVESTOR_TYPES } from "./constants";
import type { InvestorType } from "./types";

function findCountryByCode(
  allowedCountries: CountryListItem[],
  code: string,
): CountryListItem | null {
  const upper = code.toUpperCase();
  return allowedCountries.find((c) => c.value === upper) ?? null;
}

function findStateByCode(
  stateCode: string,
): { label: string; value: string } | null {
  const upper = stateCode.toUpperCase();
  return statesUS.find((s) => s.value === upper) ?? null;
}

/**
 * Gets prefill from URL search params once and applies all prefilled values to the
 * registration form (string fields immediately, country/state when countries load).
 * No props; must be rendered inside Formik and CountriesProvider.
 */
export function PrefillForm() {
  const formik = useFormikContext<FormType>();
  const [searchParams] = useSearchParams();
  const { allowedCountries } = useCountries();

  const prefillRef = useRef<ReturnType<
    typeof getPrefillFromSearchParams
  > | null>(null);
  const stringPrefillAppliedRef = useRef(false);
  const countryPrefillAppliedRef = useRef(false);

  if (prefillRef.current === null) {
    prefillRef.current = getPrefillFromSearchParams(searchParams);
  }
  const prefill = prefillRef.current;

  // Apply prefill (strings immediately, country/state when loaded)
  useLayoutEffect(() => {
    const updates: Partial<FormType> = {};
    let hasUpdates = false;

    // 1. String prefill (only once)
    if (!stringPrefillAppliedRef.current) {
      const hasEntityAuthorizationCode = searchParams.get("code") != null;
      if (prefill.firstName) updates.firstName = prefill.firstName;
      if (prefill.lastName) updates.lastName = prefill.lastName;
      if (prefill.entityName) updates.entityName = prefill.entityName;
      if (hasEntityAuthorizationCode) {
        updates.investorType = INVESTOR_TYPES.INDIVIDUAL as InvestorType;
      } else if (prefill.investorType) {
        updates.investorType = prefill.investorType as InvestorType;
      }
      stringPrefillAppliedRef.current = true;
      hasUpdates = true;
    }

    // 2. Country prefill (only once, when countries available)
    // We check !formik.values.country instead of dirty to allow prefill even if strings made form dirty
    if (!countryPrefillAppliedRef.current && allowedCountries.length > 0) {
      let countryUpdatesFound = false;

      if (prefill.country && !formik.values.country) {
        const country = findCountryByCode(allowedCountries, prefill.country);
        if (country) {
          updates.country = country;
          if (country.value === "US" && prefill.state) {
            const state = findStateByCode(prefill.state);
            if (state) updates.state = state;
          }
          countryUpdatesFound = true;
        }
      }

      if (prefill.entityCountry && !formik.values.entityCountry) {
        const entityCountry = findCountryByCode(
          allowedCountries,
          prefill.entityCountry,
        );
        if (entityCountry) {
          updates.entityCountry = entityCountry;
          if (entityCountry.value === "US" && prefill.entityState) {
            const entityState = findStateByCode(prefill.entityState);
            if (entityState) updates.entityState = entityState;
          }
          countryUpdatesFound = true;
        }
      }

      // Mark as applied if we attempted to resolve (even if no updates found, we don't retry)
      // Actually, we should only mark applied if we are sure we're done.
      // But allowedCountries shouldn't change repeatedly.
      countryPrefillAppliedRef.current = true;
      if (countryUpdatesFound) hasUpdates = true;
    }

    if (hasUpdates) {
      void formik.setValues((prev) => ({ ...prev, ...updates }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allowedCountries,
    // prefill and hasEntityAuthorizationCode are stable/refs/derived from stable
    formik.setValues,
    // We depend on values to check if fields are empty, but we don't want to re-run on every keystroke
    // However, since we use refs to gate execution, re-running is cheap/safe.
    formik.values.country,
    formik.values.entityCountry,
  ]);

  return null;
}
